<template>
  <header class="breadcrumb">
    <span>Alerts</span>
    <div style="flex-grow: 1"></div>

    <NotificationBell
          v-if="showCrumbBell"
          placement="header"
        />
  </header>

  <!-- ━━━━━━━━━━━━ DARK SECTION ━━━━━━━━━━━━ -->
  <div class="dark-section">
    <!-- Strip: Ultimi Alert -->
    <section class="strip">
      <button class="strip-toggle" @click="recentOpen = !recentOpen">
        <h2 class="strip-title">Ultimi Alert</h2>
        <span class="strip-chevron" :class="{ open: recentOpen }">›</span>
      </button>
      <div v-show="recentOpen" class="recent-alerts-scroll">
        <div
          v-for="n in recentAlerts"
          :key="n.id"
          class="alert-card"
          :style="{ borderLeftColor: severityColor(n.severity) }"
        >
          <div class="alert-card__severity-bar" :style="{ background: severityColor(n.severity) }"></div>
          <div class="alert-card__body">
            <div class="alert-card__title">{{ n.title || 'Aggiornamento' }}</div>
            <div class="alert-card__message">{{ truncate(n.message, 80) }}</div>
            <div class="alert-card__meta">
              <span class="alert-card__device">{{ n.graph_name || n.graph_id || '—' }}</span>
              <span class="alert-card__time">{{ relativeTime(n) }}</span>
            </div>
          </div>
        </div>
        <div v-if="recentAlerts.length === 0" class="recent-empty">
          Nessun alert recente.
        </div>
      </div>
    </section>

    <!-- Strip: Filtri -->
    <section class="strip">
      <div class="strip-header">
        <button class="strip-toggle" @click="filtersOpen = !filtersOpen">
          <h2 class="strip-title">Filtri</h2>
          <span class="strip-chevron" :class="{ open: filtersOpen }">›</span>
        </button>
        <button
          v-if="hasAnyFilter"
          class="btn-all"
          @click="clearAllFilters"
        >
          Azzera filtri
        </button>
      </div>

      <div v-show="filtersOpen">
      <!-- Ricerca testuale -->
      <div class="search-wrap">
        <input
          v-model="searchQuery"
          class="search-input"
          type="text"
          placeholder="Cerca per messaggio, device, utente…"
        />
      </div>

      <div class="filters-grid">
        <!-- Categoria -->
        <div class="filter-row">
          <span class="filter-row__label">Categoria</span>
          <div class="filter-chips">
            <button
              v-for="cat in categories"
              :key="cat.type"
              class="cat-card"
              :class="{ active: activeCategory === cat.type }"
              @click="selectCategory(cat.type)"
            >
              <span class="cat-card__icon">{{ categoryIcon(cat.type) }}</span>
              <span class="cat-card__label">{{ cat.label }}</span>
              <span class="cat-card__badge">{{ cat.count }}</span>
            </button>
            <div v-if="categories.length === 0" class="recent-empty">
              Nessuna categoria disponibile.
            </div>
          </div>
        </div>

        <!-- Severità -->
        <div v-if="severities.length > 0" class="filter-row">
          <span class="filter-row__label">Severità</span>
          <div class="filter-chips">
            <button
              v-for="sv in severities"
              :key="sv.severity"
              class="cat-card"
              :class="{ active: activeSeverity === sv.severity }"
              :style="activeSeverity === sv.severity ? { borderLeftColor: severityColor(sv.severity), borderLeftWidth: '3px' } : {}"
              @click="selectSeverity(sv.severity)"
            >
              <span class="cat-card__label">{{ sv.label }}</span>
              <span class="cat-card__badge">{{ sv.count }}</span>
            </button>
          </div>
        </div>

        <!-- Stato -->
        <div class="filter-row">
          <span class="filter-row__label">Stato</span>
          <div class="filter-chips">
            <button
              v-if="statusCounts.pending > 0"
              class="cat-card"
              :class="{ active: activeStatus === 'pending' }"
              :style="activeStatus === 'pending' ? { borderLeftColor: '#ff9800', borderLeftWidth: '3px' } : {}"
              @click="selectStatus('pending')"
            >
              <span class="cat-card__label">⏳ Non risolti</span>
              <span class="cat-card__badge">{{ statusCounts.pending }}</span>
            </button>
            <button
              v-if="statusCounts.resolved > 0"
              class="cat-card"
              :class="{ active: activeStatus === 'resolved' }"
              :style="activeStatus === 'resolved' ? { borderLeftColor: '#1e7e34', borderLeftWidth: '3px' } : {}"
              @click="selectStatus('resolved')"
            >
              <span class="cat-card__label">✅ Risolti</span>
              <span class="cat-card__badge">{{ statusCounts.resolved }}</span>
            </button>
          </div>
        </div>
      </div>
      </div><!-- /v-show filtri -->
    </section>
  </div>

  <!-- ━━━━━━━━━━━━ LIGHT SECTION ━━━━━━━━━━━━ -->
  <div class="alerts-wrapper">
    <div class="tbl-wrap">
      <table class="tbl">
        <thead>
          <tr>
            <th>Stato</th>
            <th>Data</th>
            <th v-if="state.isAdmin">Utente</th>
            <th v-if="state.isAdmin">Device</th>
            <th>Tipo</th>
            <th>Messaggio</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="n in filteredItems"
            :key="n.id"
            :class="[n.severity, { 'resolved': isResolved(n) }]"
          >
            <td>
              <span v-if="isResolved(n)" class="status-badge resolved">
                Risolto
              </span>
              <span v-else class="status-badge pending">
                In attesa
              </span>
            </td>
            <td class="mono">{{ formatWhen(n) }}</td>
            <td v-if="state.isAdmin" class="user-cell">{{ n.email || n.username || '—' }}</td>
            <td v-if="state.isAdmin" class="device-cell">{{ n.graph_name || n.graph_id || '—' }}</td>
            <td>
              <span :class="['label', `label--${n.severity || 'info'}`]">
                {{ n.title || 'Aggiornamento' }}
              </span>
            </td>
            <td>
              <div class="msg-cell">
                <span v-if="n.payload?.kind === 'alert' && n.title?.toLowerCase().includes('umidità')"></span>
                <span v-else-if="n.payload?.kind === 'alert' && n.title?.toLowerCase().includes('temperatura')"></span>
                {{ n.message || '—' }}
              </div>
            </td>
          </tr>
          <tr v-if="filteredItems.length === 0">
            <td :colspan="state.isAdmin ? 6 : 4" class="empty-msg">
              Nessun alert trovato.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="canFetchMore" class="load-more">
      <button class="btn-primary" @click="loadMore" :disabled="loading">
        {{ loading ? 'Caricamento...' : 'Carica altri' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNotifications } from "@/composables/useNotifications";
import NotificationBell from "./NotificationBell.vue";
import { inject, onMounted, ref, computed, type Ref } from "vue";

const isDesktop = inject<Ref<boolean>>("isDesktop")!;
const isSidebarOpen = inject<Ref<boolean>>("isSidebarOpen")!;

const showCrumbBell = computed(
  () => !isDesktop.value || (isDesktop.value && !isSidebarOpen.value)
);

const { state, fetchLatest, setMaxLimit } = useNotifications();
const loading = ref(false);
const currentLimit = ref(20);

// ─── Stato sezioni collassabili ───────────────────────────────────────────
const recentOpen = ref(false);
const filtersOpen = ref(false);

// ─── Stato filtri ─────────────────────────────────────────────────────────
const activeCategory = ref<string | null>(null);
const activeSeverity = ref<string | null>(null);
const activeStatus = ref<'resolved' | 'pending' | null>(null);
const searchQuery = ref('');

// ─── Helpers ──────────────────────────────────────────────────────────────
const isAlert = (n: any) =>
  n?.severity === "error" ||
  n?.severity === "warning" ||
  n?.payload?.kind === "alert";

const isResolved = (n: any) =>
  !!(
    n?.is_read ||
    n?.resolved ||
    n?.payload?.resolved ||
    n?.status === "resolved" ||
    n?.graph_status === "ok"
  );

function formatWhen(n: any) {
  const ts = n.createdAt ?? n.created_at;
  try {
    return ts ? new Date(ts).toLocaleString() : "";
  } catch {
    return "";
  }
}

function relativeTime(n: any) {
  const ts = n.createdAt ?? n.created_at;
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "adesso";
  if (mins < 60) return `${mins}m fa`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h fa`;
  return `${Math.floor(hrs / 24)}g fa`;
}

function truncate(str: string | undefined, max: number) {
  if (!str) return "—";
  return str.length > max ? str.slice(0, max) + "…" : str;
}

function severityColor(severity: string | undefined) {
  const map: Record<string, string> = {
    error: "#ff3b30",
    warning: "#ffcc00",
    info: "#00b3b3",
  };
  return map[severity ?? ""] ?? "#555";
}

// ─── Mappa categorie ──────────────────────────────────────────────────────
const CATEGORY_LABELS: Record<string, string> = {
  graph_status_change: "Cambi di Stato",
  graph_deleted: "Eliminati",
  graph_archived: "Archiviati",
  graph_created: "Creati",
  graph_updated: "Aggiornati",
  graph_restored: "Ripristinati",
  permission_request: "Permessi",
};

const CATEGORY_ICONS: Record<string, string> = {
  graph_status_change: "⚡",
  graph_deleted: "🗑️",
  graph_archived: "📦",
  graph_created: "✨",
  graph_updated: "✏️",
  graph_restored: "♻️",
  permission_request: "🔑",
};

function categoryIcon(type: string) {
  return CATEGORY_ICONS[type] ?? "📋";
}

function selectCategory(type: string | null) {
  activeCategory.value = activeCategory.value === type ? null : type;
}

function selectSeverity(s: string) {
  activeSeverity.value = activeSeverity.value === s ? null : s;
}

function selectStatus(s: 'resolved' | 'pending') {
  activeStatus.value = activeStatus.value === s ? null : s;
}

function clearAllFilters() {
  activeCategory.value = null;
  activeSeverity.value = null;
  activeStatus.value = null;
  searchQuery.value = '';
}

// ─── Computed ─────────────────────────────────────────────────────────────
const allAlerts = computed(() => state.items.filter(isAlert));

const recentAlerts = computed(() => allAlerts.value.slice(0, 5));

const categories = computed(() => {
  const counts: Record<string, number> = {};
  for (const n of allAlerts.value)
    if (n.type) counts[n.type] = (counts[n.type] ?? 0) + 1;
  return Object.entries(counts)
    .filter(([, c]) => c > 0)
    .map(([type, count]) => ({
      type,
      label: CATEGORY_LABELS[type] ?? type,
      count,
    }));
});

const SEVERITY_LABELS: Record<string, string> = {
  error: 'Errori',
  warning: 'Avvisi',
  info: 'Info',
  success: 'Successi',
};

const severities = computed(() => {
  const counts: Record<string, number> = {};
  for (const n of allAlerts.value)
    if (n.severity) counts[n.severity] = (counts[n.severity] ?? 0) + 1;
  return Object.entries(counts)
    .filter(([, c]) => c > 0)
    .map(([severity, count]) => ({
      severity,
      label: SEVERITY_LABELS[severity] ?? severity,
      count,
    }));
});

const statusCounts = computed(() => ({
  pending: allAlerts.value.filter(n => !isResolved(n)).length,
  resolved: allAlerts.value.filter(n => isResolved(n)).length,
}));

const hasAnyFilter = computed(() =>
  activeCategory.value !== null ||
  activeSeverity.value !== null ||
  activeStatus.value !== null ||
  searchQuery.value.trim() !== ''
);

const filteredItems = computed(() => {
  let items = allAlerts.value;
  if (activeCategory.value)
    items = items.filter(n => n.type === activeCategory.value);
  if (activeSeverity.value)
    items = items.filter(n => n.severity === activeSeverity.value);
  if (activeStatus.value === 'resolved')
    items = items.filter(n => isResolved(n));
  if (activeStatus.value === 'pending')
    items = items.filter(n => !isResolved(n));
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase();
    items = items.filter(n =>
      n.message?.toLowerCase().includes(q) ||
      n.graph_name?.toLowerCase().includes(q) ||
      n.email?.toLowerCase().includes(q) ||
      n.username?.toLowerCase().includes(q)
    );
  }
  return items;
});

const canFetchMore = computed(() =>
  state.items.length >= currentLimit.value && filteredItems.value.length > 0
);

async function loadMore() {
  loading.value = true;
  currentLimit.value += 20;
  setMaxLimit(currentLimit.value);
  await fetchLatest({ limit: currentLimit.value });
  loading.value = false;
}

onMounted(async () => {
  loading.value = true;
  setMaxLimit(currentLimit.value);
  await fetchLatest({ limit: currentLimit.value });
  loading.value = false;
});
</script>

<style scoped>
/* ━━━━━━━━━━━━ DARK SECTION ━━━━━━━━━━━━ */
.dark-section {
  background: #111;
  padding: 1.5rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.strip-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.strip-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: inherit;
  text-align: left;
}

.strip-toggle .strip-title {
  margin-bottom: 0;
}

.strip-chevron {
  font-size: 1.1rem;
  color: #555;
  line-height: 1;
  display: inline-block;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
  margin-bottom: 0.1rem;
}

.strip-chevron.open {
  transform: rotate(90deg);
}

.strip-title {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #aaa;
  margin: 0 0 0.75rem;
}

.strip-header .strip-title {
  margin-bottom: 0;
}

.btn-all {
  background: transparent;
  border: 1px solid #444;
  color: #aaa;
  font-size: 0.75rem;
  padding: 3px 12px;
  border-radius: 20px;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.btn-all:hover {
  border-color: #008f8f;
  color: #00b3b3;
}

/* ─── Recent alerts strip ─────────────────── */
.recent-alerts-scroll {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  scrollbar-width: thin;
  scrollbar-color: #333 transparent;
}

.recent-alerts-scroll::-webkit-scrollbar {
  height: 4px;
}
.recent-alerts-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.recent-alerts-scroll::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 2px;
}

.alert-card {
  display: flex;
  min-width: 220px;
  max-width: 260px;
  flex-shrink: 0;
  background: #1a1a1a;
  border-radius: 6px;
  border: 1px solid #2a2a2a;
  border-left: 3px solid #555;
  overflow: hidden;
}

.alert-card__body {
  padding: 0.65rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}

.alert-card__title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #e0e0e0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alert-card__message {
  font-size: 0.75rem;
  color: #888;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.alert-card__meta {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.1rem;
}

.alert-card__device {
  font-size: 0.7rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alert-card__time {
  font-size: 0.7rem;
  color: #555;
  white-space: nowrap;
}

.recent-empty {
  font-size: 0.8rem;
  color: #555;
  padding: 0.5rem 0;
}

/* ─── Categories strip ────────────────────── */
.categories-scroll {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cat-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  color: #ccc;
  font-size: 0.8rem;
}

.cat-card:hover {
  background: #252525;
  border-color: #555;
}

.cat-card.active {
  border-color: #008f8f;
  box-shadow: 0 0 0 2px rgba(0, 143, 143, 0.2);
  color: #00d4d4;
}

.cat-card__icon {
  font-size: 1rem;
  line-height: 1;
}

.cat-card__label {
  font-weight: 500;
}

.cat-card__badge {
  background: #2e2e2e;
  color: #888;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
}

.cat-card.active .cat-card__badge {
  background: rgba(0, 143, 143, 0.2);
  color: #00b3b3;
}

/* ━━━━━━━━━━━━ LIGHT SECTION ━━━━━━━━━━━━ */
.alerts-wrapper {
  padding: 0 1rem;
}

.tbl-wrap {
  margin-top: 20px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow-x: auto;
  background: #fff;
}

.tbl {
  width: 100%;
  border-collapse: collapse;
  font: 14px "Inter", sans-serif;
  min-width: 800px;
}

.tbl th, .tbl td {
  padding: 12px 16px;
  text-align: left;
  white-space: nowrap;
}

.tbl th {
  color: #0d0d0d;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  position: sticky;
  top: 0;
  z-index: 1;
}

.tbl tbody tr:nth-child(odd) {
  background: #f5f5f5;
}

.tbl tbody tr:nth-child(even) {
  background: #fff;
}

.tbl tbody tr:hover {
  background: #e0e0e0 !important;
}

/* Rimuove l'animazione di lampeggio eventualmente ereditata da classi globali */
.tbl tbody tr {
  animation: none !important;
}

.mono {
  font-family: ui-monospace, monospace;
  font-size: 0.85rem;
  color: #666;
}

.user-cell, .device-cell, .msg-cell {
  color: #333;
  font-weight: 500;
}

.msg-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.label {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
}

.label--error {
  background: #4a0000;
  border: 1px solid #800000;
}

.label--warning {
  background: #ff9800;
}

.label--info {
  background: #008f8f;
}

.status-badge {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.resolved {
  background: #e6f4ea;
  color: #1e7e34;
  border: 1px solid #c3e6cb;
}

.status-badge.pending {
  background: #fff3e0;
  color: #e65100;
  border: 1px solid #ffe0b2;
}

.empty-msg {
  text-align: center;
  color: #999;
  padding: 4rem;
  background: #fff !important;
}

.load-more {
  display: flex;
  justify-content: center;
  margin: 2rem 0;
}

.btn-primary {
  background: #008f8f;
  color: #fff;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  font: 600 14px "Inter", sans-serif;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #00a7a7;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 143, 143, 0.2);
}

.btn-primary:active {
  background: #007777;
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .categories-scroll {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    scrollbar-width: thin;
    scrollbar-color: #333 transparent;
  }

  .cat-card {
    flex-shrink: 0;
  }
}

@media (max-width: 1024px) {
  .alerts-wrapper {
    padding: 0 1rem;
  }
}

/* ─── Filtri multipli ─────────────────────── */
.search-wrap {
  margin-bottom: 0.75rem;
}

.search-input {
  width: 100%;
  box-sizing: border-box;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #ddd;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  outline: none;
  transition: border-color 0.15s;
}

.search-input:focus {
  border-color: #008f8f;
}

.search-input::placeholder {
  color: #555;
}

.filters-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
  align-items: flex-start;
}

.filter-row {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.filter-row__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #555;
  white-space: nowrap;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
</style>
